import { isDeliveryRouteCompleted } from '@/components/orders/delivery-share.utils'
import { adminRouteTitleHead } from '@/lib/admin-route-title'
import { canManageSalesOrderField } from '@/lib/sales-order-access'
import { deliveryShareQueries } from '@/lib/services/delivery-shares/delivery-shares.query'
import { deliveryShareIdSchema } from '@/lib/services/delivery-shares/delivery-shares.schema'
import { salesOrderQueries } from '@/lib/services/sales-orders/sales-orders.query'
import { createFileRoute, notFound, Outlet, useChildMatches } from '@tanstack/react-router'
import { DeliveryRouteDetail } from './-delivery-route-detail'
import { DeliveryRouteEdit } from './-delivery-route-edit'

export const Route = createFileRoute(
  '/_authenticated/$orgId/sales-orders/delivery-routes/$deliveryShareId'
)({
  component: DeliveryRouteDetailPage,
  head: adminRouteTitleHead,
  params: {
    parse: (params) => deliveryShareIdSchema.parse(params),
  },
  loader: loadDeliveryRoute,
})

export async function loadDeliveryRoute({ context, params, requireEdit = false }) {
  if (!context.ability.can('list', 'sales-orders')) throw notFound()
  const canManageRoute = canManageSalesOrderField(context.ability, 'fulfillment')

  if (requireEdit && !canManageRoute) throw notFound()

  const prefetches = [
    context.queryClient.ensureQueryData(
      deliveryShareQueries.show({
        organizationId: params.orgId,
        deliveryShareId: params.deliveryShareId,
      })
    ),
  ]

  if (requireEdit) {
    prefetches.push(
      context.queryClient.ensureQueryData(
        salesOrderQueries.deliveryRouteOrders({
          organizationId: params.orgId,
        })
      )
    )
  }

  const [deliveryShare] = await Promise.all(prefetches)

  if (
    requireEdit &&
    (deliveryShare?.status === 'expired' || isDeliveryRouteCompleted(deliveryShare))
  ) {
    throw notFound()
  }

  return { deliveryShare, isReadOnly: !canManageRoute }
}

export function DeliveryRouteDetailPage() {
  const hasChildMatch = useChildMatches({
    select: (matches) => matches.length > 0,
  })

  if (hasChildMatch) {
    return <Outlet />
  }

  return <DeliveryRouteDetail routeApi={Route} />
}

export function DeliveryRouteEditPage({ routeApi = Route }) {
  return <DeliveryRouteEdit routeApi={routeApi} />
}
